<!--
  @description Toggle switch for boolean settings. Use in place of a standalone checkbox
  when the control represents an on/off or enabled/disabled state — for example, toggling
  admin visibility of a pack or enabling a game option. Fully compatible with `v-model`.

  When `disabled` is true the click is intercepted and the model value is left unchanged,
  making the switch visually inert without relying on the native `disabled` attribute alone.

  @example
  <Switch
    id="pack-published"
    title="Published"
    v-model="form.published"
    :disabled="!canPublish"
  />

  @accessibility The toggle is implemented as a checkbox `<input>` inside a styled
  `<label>`, so it inherits native keyboard focus and space-bar activation. The visible
  label uses a separate `<label :for="id">` so assistive technologies announce the control
  name. Do not omit `title` without providing an alternative `aria-label` at the usage site.
-->
<template>
  <div class="switch">
    <label class="switch__toggle" ref="toggler">
      <input
        type="checkbox"
        ref="input"
        :id="id"
        :checked="modelValue"
        class="switch__input"
        @click="handleClick"
      />
      <div
        class="switch__slider switch__slider--round"
        :class="{ 'switch__slider--disabled': disabled }"
      ></div>
    </label>
    <label v-if="title" :for="id" class="switch__label">{{ title }}</label>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  /** Unique HTML id — wired to the hidden checkbox and matched by the visible label's `for`. */
  id: {
    type: String,
    required: true
  },
  /** Current on/off state. Bind with `v-model` for two-way updates. */
  modelValue: {
    type: Boolean,
    required: true
  },
  /** Descriptive label rendered to the right of the toggle. Omit for icon-only switches. */
  title: {
    type: String,
    required: false
  },
  /**
   * When true, click events are suppressed and the model value is never toggled.
   * The slider receives a `switch__slider--disabled` BEM modifier for visual feedback.
   */
  disabled: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()

function handleClick(e: Event) {
  if (props.disabled) {
    e.preventDefault()
    return
  }

  emit('update:modelValue', !props.modelValue)
}
</script>

<style lang="scss" scoped>
/* slider switch css */
.switch {
  display: flex;
  align-items: center;
  margin: 0.5rem;

  @include breakpoint-sm {
    flex-direction: column;
    gap: $half-spacing;
  }

  &__toggle {
    display: inline-block;
    height: 28px;
    position: relative;
    width: 50px;
    margin-right: 0.5rem;
  }

  &__label {
    font-size: 0.9rem;
    font-weight: 450;
    color: var(--text);
  }

  &__input {
    display: none;
  }

  &__slider {
    background-color: var(--primary);
    bottom: 0;
    cursor: pointer;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: 0.4s;

    &::before {
      background-color: var(--input-background);
      bottom: 4px;
      content: '';
      height: 20px;
      left: 4px;
      position: absolute;
      transition: 0.4s;
      width: 20px;
    }

    &--round {
      border-radius: 28px;

      &::before {
        border-radius: 50%;
      }
    }

    &--disabled {
      // Visual-only inert state; click suppression is handled in handleClick
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.switch__input:checked + .switch__slider {
  background-color: var(--secondary);
}

.switch__input:checked + .switch__slider::before {
  transform: translateX(21px);
}
</style>
